import { inject } from '@angular/core';
import { ActivityEntityName, Bill, EntityViewDisplay, FeatureFlags, FinanceSecurityRight, FINANCIAL_DOCUMENT_PAYMENT_OPTION, InvoiceType, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { Command } from '@workbuddy/ui/model';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { billExportCommands } from './commands/bill.export.command';
import { BillAccountingCellRendererComponent } from './presentation/bill.accounting.cell.renderer';
import { billAccountingTag, billStatusTag, billTypeTag } from './presentation/bill.status.colour';

// Registered when this manifest module is loaded at bootstrap.
// Consumers resolve manifests via getManifest/EntityManifestService only.
// This is the Tenant manifest; portals get no Bill UI until declared under `domains`.
export const billManifest = registerManifest<Bill>({
  entity: ActivityEntityName.Bill,
  slug: 'bills',
  featureFlag: FeatureFlags.FinanceDocsBillV2,
  menu: {
    group: 'finance',
    label: 'Bills',
    icon: 'fa-money',
    order: 20,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(FinanceSecurityRight.CanAccessBills),
  },

  routes: {
    root: '/finance/bills',
    // Supplier credit notes and prepayments are bills in the same collection and open here too.
    record: id => entityRecordUrl('bills', 'preview', id),
  },

  board: {
    // Kanban is not meaningful for financial documents; locked manage preview is a per-view toggle.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    // Document-shaped: the selection shows in the wide ~50/50 aside, not a narrow quickview.
    panel: { kind: 'preview' },
    defaults: {
      // List action, not a surface operation: operations only mount create/edit/preview.
      header: {
        title: 'Bills',
        primaryCommands: billExportCommands,
        // Legacy Bills' split +: the main click stays a plain bill. The caret opens this same editor with a type.
        addCommands: [
          new Command({ label: 'New Credit Note', data: { type: InvoiceType.CreditNoteSupplier } }),
          new Command({ label: 'New Prepayment', data: { type: InvoiceType.PrepaymentSupplier } }),
        ],
      },
      table: {
        overrideCellRenderer: (_context, _metadata, field, renderer) => field.name === 'integration'
          ? { ...renderer, cellRenderer: BillAccountingCellRendererComponent }
          : renderer,
      },
    },
  },

  presentation: {
    // Board cards carry lifecycle, document type, and accounting result. Settlement is deliberately
    // absent here: the card already prints the outstanding amount, so a Paid pill beside it is
    // redundant — the document header, where no amount is in view, shows it.
    tags: {
      status: billStatusTag,
      type: billTypeTag,
      integration: billAccountingTag,
    },
  },

  surfaces: {
    [SurfaceType.FinancialDocument]: () => import('./surface/manage/bill.manage.surface').then(m => m.billSurface),
  },
  operations: {
    preview: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessBills),
      // The header's payment actions (card, allocate credit) are decided with the record, not after it.
      readOptions: [FINANCIAL_DOCUMENT_PAYMENT_OPTION],
    },
    create: { surface: SurfaceType.FinancialDocument, authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.ManageBills) },
    // Read access mounts the document; write rights and lifecycle belong to surface `editableWhen`.
    edit: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessBills),
      // The header's payment actions (card, allocate credit) are decided with the record, not after it.
      readOptions: [FINANCIAL_DOCUMENT_PAYMENT_OPTION],
    },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/bill.activities.relation').then(m => m.billActivitiesBoardBuilder),
  },
});
